/**
 * Parses the 400 payload from POST .../imported-testcases into a grouped
 * structure the Add-to-Suite / Import Preview modals can render.
 *
 * Prefer `errors[]` ({ testCaseId, field, message }). Fall back to splitting
 * the concatenated `message` string. Never return that raw blob for display.
 */

const FIELD_LABELS = new Map<string, string>([
  ["testCaseType", "Test case type"],
  ["subType", "Sub type"],
  ["testScenario", "Test scenario"],
  ["prerequisites", "Prerequisites"],
  ["expectedResult", "Expected result"],
  ["testCaseId", "Test case ID"],
]);

const REQUIRED_LINE =
  /^(\S+)\s+is required for testCaseId\s+(\S+)\s*$/i;
const DUPLICATE_LINE =
  /^Duplicate testCaseId in request:\s*(\S+)\s*$/i;

const GENERIC_SAVE_FAILURE = "Failed to save test cases.";
const GENERIC_DUPLICATE_ONLY =
  "Could not add test cases because some are duplicated. Remove duplicates and try again.";

export interface ImportValidationGroup {
  testCaseId: string;
  fieldLabels: string[];
  isDuplicate: boolean;
}

export interface ParsedImportValidation {
  groups: ImportValidationGroup[];
  duplicateIds: string[];
  caseCount: number;
  issueCount: number;
  genericMessage: string;
  hasStructuredErrors: boolean;
}

export function importFieldLabel(field: string): string {
  const key = field.trim();
  return FIELD_LABELS.get(key) ?? key;
}

export function parseImportValidationErrors(
  err: unknown
): ParsedImportValidation {
  const body = unwrapErrorBody(err);
  const message = readString(body, "message");
  const items = readErrorItems(body);

  const acc = createAccumulator();

  for (const item of items) {
    addRawItem(acc, item);
  }

  const messageLines = splitMessageLines(message);
  for (const line of messageLines) {
    const duplicateId = matchDuplicateLine(line);
    if (duplicateId) {
      addDuplicate(acc, duplicateId);
      continue;
    }
    if (items.length > 0) {
      continue;
    }
    const required = matchRequiredLine(line);
    if (required) {
      addField(acc, required.testCaseId, required.field);
    }
  }

  const groups = buildGroups(acc);
  if (groups.length > 0) {
    return {
      groups,
      duplicateIds: [...acc.duplicateIds],
      caseCount: groups.length,
      issueCount: acc.issueCount,
      genericMessage: "",
      hasStructuredErrors: true,
    };
  }

  return {
    groups: [],
    duplicateIds: [],
    caseCount: 0,
    issueCount: 0,
    genericMessage: pickGenericMessage(message),
    hasStructuredErrors: false,
  };
}

export function formatImportErrorSummary(
  parsed: ParsedImportValidation
): string {
  if (!parsed.hasStructuredErrors) {
    return parsed.genericMessage || GENERIC_SAVE_FAILURE;
  }
  if (parsed.issueCount === 0 && parsed.duplicateIds.length > 0) {
    return GENERIC_DUPLICATE_ONLY;
  }
  const cases = pluralize(parsed.caseCount, "test case", "test cases");
  const fields = pluralize(
    parsed.issueCount,
    "missing field",
    "missing fields"
  );
  return `Could not add ${cases} (${fields}). Fill the required fields and try again.`;
}

export interface ImportValidationView {
  groups: ImportValidationGroup[];
  summary: string;
  duplicateNote: string;
  genericMessage: string;
}

export function toImportValidationView(
  err: unknown,
  fallback: string
): ImportValidationView {
  const parsed = parseImportValidationErrors(err);
  if (parsed.hasStructuredErrors) {
    return {
      groups: parsed.groups,
      summary: formatImportErrorSummary(parsed),
      duplicateNote: formatDuplicateNote(parsed.duplicateIds),
      genericMessage: "",
    };
  }
  return {
    groups: [],
    summary: "",
    duplicateNote: "",
    genericMessage: parsed.genericMessage || fallback,
  };
}

export function formatDuplicateNote(duplicateIds: string[]): string {
  if (duplicateIds.length === 0) {
    return "";
  }
  const label =
    duplicateIds.length === 1 ? "Duplicated test case" : "Duplicated test cases";
  return `${label}: ${duplicateIds.join(", ")}.`;
}

interface RawItem {
  testCaseId: string;
  field: string;
  message: string;
}

interface Accumulator {
  order: string[];
  fieldsById: Map<string, string[]>;
  duplicateIds: string[];
  duplicateSet: Set<string>;
  issueCount: number;
}

function createAccumulator(): Accumulator {
  return {
    order: [],
    fieldsById: new Map<string, string[]>(),
    duplicateIds: [],
    duplicateSet: new Set<string>(),
    issueCount: 0,
  };
}

function ensureGroup(acc: Accumulator, testCaseId: string): string[] {
  const existing = acc.fieldsById.get(testCaseId);
  if (existing) {
    return existing;
  }
  const fields: string[] = [];
  acc.fieldsById.set(testCaseId, fields);
  acc.order.push(testCaseId);
  return fields;
}

function addField(acc: Accumulator, testCaseId: string, field: string): void {
  const id = testCaseId.trim();
  const key = field.trim();
  if (!id || !key) {
    return;
  }
  const fields = ensureGroup(acc, id);
  if (fields.includes(key)) {
    return;
  }
  fields.push(key);
  acc.issueCount += 1;
}

function addDuplicate(acc: Accumulator, testCaseId: string): void {
  const id = testCaseId.trim();
  if (!id || acc.duplicateSet.has(id)) {
    return;
  }
  acc.duplicateSet.add(id);
  acc.duplicateIds.push(id);
  ensureGroup(acc, id);
}

function addRawItem(acc: Accumulator, item: RawItem): void {
  if (isDuplicateMessage(item.message) || isDuplicateField(item)) {
    addDuplicate(acc, item.testCaseId);
    return;
  }
  const field =
    item.field || matchRequiredLine(item.message)?.field || "";
  addField(acc, item.testCaseId, field);
}

function buildGroups(acc: Accumulator): ImportValidationGroup[] {
  return acc.order.map((testCaseId) => {
    const fields = acc.fieldsById.get(testCaseId) ?? [];
    return {
      testCaseId,
      fieldLabels: fields.map((field) => importFieldLabel(field)),
      isDuplicate: acc.duplicateSet.has(testCaseId),
    };
  });
}

function unwrapErrorBody(err: unknown): Record<string, unknown> | null {
  const root = asRecord(err);
  if (!root) {
    return null;
  }
  if ("error" in root) {
    const nested = root["error"];
    if (typeof nested === "string") {
      return { message: nested };
    }
    const nestedRecord = asRecord(nested);
    if (nestedRecord) {
      return nestedRecord;
    }
  }
  return root;
}

function readErrorItems(body: Record<string, unknown> | null): RawItem[] {
  if (!body || !("errors" in body) || !Array.isArray(body["errors"])) {
    return [];
  }
  const items: RawItem[] = [];
  for (const entry of body["errors"]) {
    const item = toRawItem(entry);
    if (item) {
      items.push(item);
    }
  }
  return items;
}

function toRawItem(value: unknown): RawItem | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const testCaseId = readString(record, "testCaseId").trim();
  if (!testCaseId) {
    return null;
  }
  return {
    testCaseId,
    field: readString(record, "field").trim(),
    message: readString(record, "message"),
  };
}

function readString(
  record: Record<string, unknown> | null,
  key: string
): string {
  if (!record || !(key in record)) {
    return "";
  }
  const value = record[key];
  return typeof value === "string" ? value : "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function splitMessageLines(message: string): string[] {
  if (!message) {
    return [];
  }
  return message
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function matchRequiredLine(
  line: string
): { field: string; testCaseId: string } | null {
  const match = REQUIRED_LINE.exec(line.trim());
  if (!match) {
    return null;
  }
  return { field: match[1], testCaseId: match[2] };
}

function matchDuplicateLine(line: string): string | null {
  const match = DUPLICATE_LINE.exec(line.trim());
  return match ? match[1] : null;
}

function isDuplicateMessage(message: string): boolean {
  return DUPLICATE_LINE.test(message.trim()) || /duplicate testCaseId/i.test(message);
}

function isDuplicateField(item: RawItem): boolean {
  return item.field === "testCaseId" && /duplicate/i.test(item.message);
}

function pickGenericMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed || isBulkValidationMessage(trimmed)) {
    return "";
  }
  return trimmed;
}

function isBulkValidationMessage(message: string): boolean {
  return (
    message.includes("\n") &&
    (/is required for testCaseId/i.test(message) ||
      /Duplicate testCaseId in request/i.test(message))
  );
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
